/*
 * 获取代理链接
 * @param params - 参数
 * @param params.locale - 语言参数
 * @param params.suffix - 代理后缀
 * @param params.path - 子路由路径
 * @returns 代理链接
 */

/*
 * nginx 代理了 testwww.metal.com 当访问 www.metal.com/en/webinar 时，会代理到 webinar.metal.com/en
 * nginx 代理了 testwww.metal.com 当访问 www.metal.com/en/price-center 时，会代理到 price-center.metal.com/en
 * 当访问 www.metal.com/en/webinar/xxxx时，会代理到 webinar.metal.com/en/xxxx
 * 所以nextjs Link 标签的 src 前缀存在额外的 en 参数会导致无法匹配，所以需要这个函数 来重写代理链接，去掉 en 参数以及增加开发环境的 项目参数如 webinar 、price-center 等
 */
export interface UrlPathInfo {
  /** 完整 URL */
  fullUrl: string
  /** 完整路径（如：/en/xxxx） */
  pathname: string
  /** 语言代码（如：en） */
  locale: string
  /** 路径段数组（不包含 locale，如：['xxxx', 'detail']） */
  segments: string[]
  /** 协议（http/https） */
  protocol: string
  /** 域名（如：xxx.metal.com） */
  host: string
}

const getProxyLink = (params: { locale: string; path: string }) => {
  const { locale, path } = params
  const suffix = process.env.NEXT_PUBLIC_DEPLOY_APP_NAME
  const isDev = process.env.NEXT_PUBLIC_ENV === "development"

  if (isDev) {
    return `/${locale}/${path}`
  }
  return `/${locale}/${suffix}/${path}`
}

/**
 * 从 Next.js headers 中解析 URL 路径信息
 * @returns URL 路径详细信息，包含 locale 和路径段
 */
async function parseUrlFromHeaders(): Promise<UrlPathInfo> {
  const { headers } = await import("next/headers")
  const headersList = await headers()

  const pathname = headersList.get("x-pathname") || "/"
  const protocol = headersList.get("x-forwarded-proto") || "http"
  const host = headersList.get("host") || ""

  // 解析路径段：移除首尾斜杠，然后分割
  const pathSegments = pathname
    .replace(/^\/|\/$/g, "")
    .split("/")
    .filter(Boolean)

  // 第一段通常是 locale（如 en, zh-CN 等）
  const locale = pathSegments[0] || ""

  // 剩余的路径段（不包含 locale）
  const segments = pathSegments.slice(1)

  return {
    fullUrl: `${protocol}://${host}${pathname}`,
    pathname,
    locale,
    segments,
    protocol,
    host,
  }
}

/**
 * 从 Next.js headers 中获取当前请求的完整 URL
 * @returns 完整的 URL 字符串，格式：protocol://host/pathname
 */
async function getCurrentUrlFromHeaders(): Promise<string> {
  const info = await parseUrlFromHeaders()
  const appName = process.env.NEXT_PUBLIC_DEPLOY_APP_NAME
  const dev_url = info.fullUrl
  const url =
    process.env.NEXT_PUBLIC_ENV === "development"
      ? dev_url
      : `${info.protocol}://${info.host}/${info.locale}/${appName}/${info.segments.join("/")}`
  return url
}

export { getCurrentUrlFromHeaders, getProxyLink }
